import { Mic, Camera, Compass, X } from "lucide-react";

/**
 * AriaClarifyDialog — shown when the unified router returns intent="clarify".
 *
 * The clinician sees exactly what STT heard (no rewriting) plus three
 * one-tap choices. Fully controlled by AriaContext; this component is pure
 * presentation.
 *
 * Feature-flagged: only rendered when REACT_APP_UNIFIED_ROUTER=1 and the
 * router has explicitly set `clarify` state on the context.
 */
export default function AriaClarifyDialog({ open, transcript, onAsk, onCamera, onCancel }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="aria-clarify-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="aria-clarify-title"
    >
      <div className="w-full max-w-md bg-[var(--pa-surface,#111)] border border-[var(--pa-border,#333)] rounded-2xl shadow-2xl p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--pa-gold,#d4af37)]">
              ARIA
            </div>
            <h2
              id="aria-clarify-title"
              className="mt-1 font-serif text-lg text-[var(--pa-text,#fff)]"
            >
              How can I help with that?
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            data-testid="aria-clarify-close"
            aria-label="Cancel"
            className="text-[var(--pa-text-muted,#888)] hover:text-[var(--pa-text,#fff)] transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="mt-3 rounded-md border border-[var(--pa-border,#333)] bg-[var(--pa-bg-2,#0b0b0b)] p-3">
          <div className="text-[10px] uppercase tracking-widest text-[var(--pa-text-dim,#666)] mb-1">
            I heard
          </div>
          <div
            className="text-sm text-[var(--pa-text,#fff)] italic"
            data-testid="aria-clarify-transcript"
          >
            {transcript || "—"}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-2">
          <button
            type="button"
            onClick={onAsk}
            data-testid="aria-clarify-ask"
            className="w-full inline-flex items-center gap-3 rounded-md border border-[var(--pa-gold,#d4af37)]/60 bg-[var(--pa-gold,#d4af37)]/10 px-4 py-3 text-left text-[var(--pa-text,#fff)] hover:bg-[var(--pa-gold,#d4af37)]/20 transition"
          >
            <Mic size={18} className="text-[var(--pa-gold,#d4af37)] shrink-0" />
            <div>
              <div className="text-sm font-semibold">Ask a clinical question</div>
              <div className="text-xs text-[var(--pa-text-muted,#aaa)]">
                Route this to Quick Assist
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={onCamera}
            data-testid="aria-clarify-camera"
            className="w-full inline-flex items-center gap-3 rounded-md border border-[var(--pa-border,#333)] bg-[var(--pa-bg-2,#0b0b0b)] px-4 py-3 text-left text-[var(--pa-text,#fff)] hover:border-[var(--pa-gold,#d4af37)] transition"
          >
            <Camera size={18} className="text-[var(--pa-text-muted,#aaa)] shrink-0" />
            <div>
              <div className="text-sm font-semibold">Open the camera</div>
              <div className="text-xs text-[var(--pa-text-muted,#aaa)]">
                Start a photo assessment
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={onCancel}
            data-testid="aria-clarify-cancel"
            className="w-full inline-flex items-center gap-3 rounded-md border border-[var(--pa-border,#333)] bg-[var(--pa-bg-2,#0b0b0b)] px-4 py-3 text-left text-[var(--pa-text,#fff)] hover:border-[var(--pa-gold,#d4af37)] transition"
          >
            <Compass size={18} className="text-[var(--pa-text-muted,#aaa)] shrink-0" />
            <div>
              <div className="text-sm font-semibold">Navigate somewhere</div>
              <div className="text-xs text-[var(--pa-text-muted,#aaa)]">
                Cancel — I'll re-tap and say where to go
              </div>
            </div>
          </button>
        </div>

        <div className="mt-3 text-[10px] text-[var(--pa-text-dim,#666)] text-center">
          Preview — General clinical education only. No PHI.
        </div>
      </div>
    </div>
  );
}
